import { useEffect, useMemo, useState } from "react";

import {
  TextField,
  Select,
  Button,
  DatePicker,
  Typography,
  InfoPanel,
  Checkbox,
  Modal,
} from "@king-ict/eupisi-ui/components";
import { IconWrapper } from "@king-ict/eupisi-ui/components";
import { ReactComponent as Info } from "@king-ict/eupisi-ui/components/Icon/Info.svg";
import { CodeBook } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, hrDate } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import {
  mapOibDataToSchoolCandidate,
  buildOibPersonDataFromSchoolCandidate,
} from "./utils";
import { searchRequestMetadata } from "../../api/Request/RequestApi";
import {
  getSchoolCandidateById,
  saveSchoolCandidate,
} from "../../api/SchoolCandidate/SchoolCandidateApi";
import { useTypedSelector } from "../../app/rootReducer";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  ToolbarTop,
  ToolbarEdit,
  FormCard,
  AddressSelect,
  InstitutionSelect,
} from "../../components";
import { OIB_REGEX } from "../../constants";
import { useAuth, useDidUpdateEffect } from "../../hooks";
import {
  OibServiceResult,
  ValidationFailure,
  ServiceResultStatus,
  Address,
  OibIdentification,
} from "../../models";
import type { RequestMetadata } from "../../models/Request";
import { PersonAddress } from "../../models/Request/PersonAddress";
import {
  getDefaultSchoolCandidate,
  SchoolCandidate,
  SchoolCandidateStatus,
} from "../../models/SchoolCandidate";
import { ClaimType } from "../../models/User";
import {
  useGetAlternativeCodeReasonListQuery,
  useGetAlternativeCodeTypeListQuery,
  useGetCountyListForUserQuery,
  useGetCountiesQuery,
  useGetGenderListQuery,
  useGetInstitutionListForUserQuery,
  useGetSchoolCandidateAddReasonListQuery,
  useGetSchoolCandidateCancelReasonListQuery,
  useGetSchoolYearCurrentQuery,
} from "../../queries/codeBookQuery";
import { useLazyGetSchoolCandidateOibQuery } from "../../queries";
import {
  schoolCandidateDetailUrl,
  schoolCandidateInstitutionAdminDetailUrl,
  schoolCandidateInstitutionAdminListRoute,
  schoolCandidateRoute,
} from "../../routes/routes";
import {
  resetIsEdited,
  resetRedirectURL,
  setIsEdited,
  setSaveOK,
} from "../../slices/applicationSlice";
import {
  FormatUtils,
  MsgUtils,
  renderOibServiceInfoComponent,
} from "../../utils";
import { INSTITUTION_ROLES } from "./pages/components/constants";
import { hasActiveRequestOrDraft } from "../Request/utils";
import { OibIdentitySection } from "../oib";
import {
  ConfirmModal,
  type ConfirmModalProps,
} from "../../components/FormControls/ConfirmModal";

export function SchoolCandidateEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { data: currentSchoolYear } = useGetSchoolYearCurrentQuery();
  const { data: authCounties } = useGetCountyListForUserQuery();
  const { data: allCounties } = useGetCountiesQuery();
  const { data: institutions } = useGetInstitutionListForUserQuery();
  const [getSchoolCandidateOibTrigger] = useLazyGetSchoolCandidateOibQuery();

  const isNewCandidate = id === "0";
  const isEditingExistingCandidate = +id > 0;
  const defaultSchoolCandidate = getDefaultSchoolCandidate();
  const [isLoading, setIsLoading] = useState(true);
  const [schoolCandidate, setSchoolCandidate] = useState<SchoolCandidate>(
    defaultSchoolCandidate
  );

  const canInstitutionUserEdit = useMemo(
    () => INSTITUTION_ROLES.includes(auth.currentRole?.role ?? 0),
    [auth]
  );

  const [showOibAlert, setShowOibAlert] = useState<string>();
  const [showOibSuccess, setShowOibSuccess] = useState<string>();

  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [oibServiceResult, setOibServiceResult] =
    useState<OibServiceResult<SchoolCandidate>>();

  const { data: genders } = useGetGenderListQuery();
  const [statuses] = useState<CodeBook[]>(
    MsgUtils.getEnumToCodeBook(SchoolCandidateStatus, "schoolCandidateStatus")
  );
  const { data: schoolCandidateAlternativeCodeReasons } =
    useGetAlternativeCodeReasonListQuery();
  const { data: schoolCandidateAlternativeCodeTypes } =
    useGetAlternativeCodeTypeListQuery();
  const { data: schoolCandidateAddReasons } =
    useGetSchoolCandidateAddReasonListQuery();
  const { data: schoolCandidateCancelReasons } =
    useGetSchoolCandidateCancelReasonListQuery();

  const redirectURL = useTypedSelector(
    (state) => state.application.redirectURL
  );

  const [requestMetadata, setRequestMetadata] = useState<RequestMetadata[]>(
    []
  );

  const hasRequestOrDraft = hasActiveRequestOrDraft(requestMetadata);

  const [showCountyWarningModal, setShowCountyWarningModal] = useState(false);
  const [cancelReasonConfirmState, setCancelReasonConfirmState] =
    useState<ConfirmModalProps["confirmState"]>();

  const shouldConfirmCancelReason =
    schoolCandidate.schoolCandidateCancelReasonId != null;

  const handleFieldChange = (
    field: keyof SchoolCandidate,
    value: string | number | boolean | Date | undefined,
    options?: {
      parseAsNumber?: boolean;
      allowEmpty?: boolean;
      customMapping?: (value: any) => any;
    }
  ) => {
    let processedValue = value;
    if (processedValue) {
      if (options?.parseAsNumber && !(processedValue instanceof Date)) {
        processedValue = Number.parseInt(processedValue.toString());
      } else if (options?.customMapping) {
        processedValue = options.customMapping(processedValue);
      }
    }

    const finalValue =
      options?.allowEmpty === false && !processedValue
        ? undefined
        : processedValue;

    setSchoolCandidate((prev) => ({
      ...prev,
      [field]: finalValue,
    }));
    dispatch(setIsEdited());
  };

  useDidUpdateEffect(() => {
    if (isNewCandidate) {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
      };
      if (currentSchoolYear) {
        newSchoolCandidate.schoolYearId = currentSchoolYear.id;
      }
      if (authCounties && authCounties.length > 0) {
        newSchoolCandidate.uoguCountyId = authCounties[0].id;
      }
      if (
        auth &&
        auth.currentRole?.claimType === ClaimType.Institution &&
        institutions &&
        institutions.length > 0
      ) {
        newSchoolCandidate.institutionId = institutions[0].id;
      }
      setSchoolCandidate(newSchoolCandidate);
    }
  }, [currentSchoolYear, auth, authCounties, institutions]);

  const loadRequestMetadata = async (schoolCandidateId: number) => {
    const result = await searchRequestMetadata({
      institutionIds: [],
      schoolCandidateId,
    });
    setRequestMetadata(result?.results ?? []);
  };

  useEffect(() => {
    (async () => {
      if (isEditingExistingCandidate) {
        const result = await getSchoolCandidateById(+id);
        if (result) {
          setSchoolCandidate(result);
        }

        await loadRequestMetadata(+id);
      }
      setIsLoading(false);
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePermanentAddressChange = (newAddress: Address) => {
    if (newAddress) {
      const newResidence: PersonAddress = {
        ...(schoolCandidate?.residence ?? { id: 0 }),
        permanentAddress: newAddress,
      };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        residence: newResidence,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };

  const handleTemporaryAddressChange = (newAddress: Address) => {
    if (newAddress) {
      const newResidence: PersonAddress = {
        ...(schoolCandidate?.residence ?? { id: 0 }),
        temporaryAddress: newAddress,
      };
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        residence: newResidence,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };

  const isConnected = !!(schoolCandidate.isOibConnected || oibServiceResult);
  const handleConnectDisconnectOibSystem = async () => {
    if (isConnected && isEditingExistingCandidate) {
      // Handle disconnect - only for existing candidates
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        isOibConnected: false,
      };
      setSchoolCandidate(newSchoolCandidate);
      dispatch(setIsEdited());
      setOibServiceResult(undefined);
      setShowOibSuccess(undefined);
    } else if (schoolCandidate.oib) {
      // Handle connect
      const oibIdentification: OibIdentification = {
        oib: schoolCandidate.oib,
        firstName: schoolCandidate.firstName,
        lastName: schoolCandidate.lastName,
      };
      await updateOibSystemData(oibIdentification, true);
    }
  };
  const updateOibSystemData = async (
    oibIdentification: OibIdentification,
    forStorage: boolean
  ) => {
    setShowOibAlert(undefined);
    setShowOibSuccess(undefined);
    if (OIB_REGEX.test(oibIdentification.oib!)) {
      try {
        const result = await getSchoolCandidateOibTrigger(
          oibIdentification
        ).unwrap();

        if (
          (result.status & ServiceResultStatus.Success) ===
            ServiceResultStatus.Success &&
          result.item?.oib
        ) {
          if (isNewCandidate) {
            // For new candidates, automatically apply the data
            const newSchoolCandidate = mapOibDataToSchoolCandidate(
              schoolCandidate,
              result
            );
            setSchoolCandidate(newSchoolCandidate);
            dispatch(setIsEdited());
            setShowOibSuccess(t("common.oibServiceResultStatus.successApply"));
          } else if (forStorage) {
            setOibServiceResult(result);
            setShowOibSuccess(MsgUtils.getOibErrorMessage(result.status));
          } else {
            const newSchoolCandidate = mapOibDataToSchoolCandidate(
              schoolCandidate,
              result
            );
            setSchoolCandidate(newSchoolCandidate);
            dispatch(setIsEdited());
            setShowOibSuccess(MsgUtils.getOibErrorMessage(result.status));
          }

          return true;
        } else {
          setOibServiceResult(undefined);
          if (
            result.status === ServiceResultStatus.OibExistsInSchoolCandidate &&
            result.item?.uoguCountyId
          )
            setShowOibAlert(
              t(MsgUtils.getOibErrorMessage(result.status), {
                countyName: authCounties?.find(
                  (v) => v.id === result?.item?.uoguCountyId
                )?.name,
              })
            );
          else setShowOibAlert(t(MsgUtils.getOibErrorMessage(result.status)));
        }
      } catch {
        setOibServiceResult(undefined);
        setShowOibAlert(t("common.oibServiceResultStatus.unknown"));
      }
    }

    return false;
  };
  const handleUOGUCountyChange = (e: any) => {
    const countyId = Number.parseInt(e.target.value);
    if (!isNaN(countyId)) {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        uoguCountyId: countyId,
        institutionId: undefined,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };

  const performSave = async () => {
    const result = await saveSchoolCandidate(schoolCandidate);

    if (result && result.result !== undefined) {
      if (result.isValid) {
        dispatch(resetIsEdited());
        dispatch(setSaveOK());

        if (isEditingExistingCandidate) {
          await loadRequestMetadata(schoolCandidate.id);
        }

        if (authCounties?.find((x) => x.id === schoolCandidate?.uoguCountyId)) {
          let redirectUrl = schoolCandidateDetailUrl(result.result?.id);

          if (
            window.location.pathname.includes(
              schoolCandidateInstitutionAdminListRoute
            )
          ) {
            redirectUrl = schoolCandidateInstitutionAdminDetailUrl(
              result.result?.id
            );
          }

          history.replace(redirectUrl);
        } else {
          if (redirectURL) {
            dispatch(resetRedirectURL());
            history.push(redirectURL);
          } else {
            history.replace(schoolCandidateRoute);
          }
        }
      }

      setErrors(result.validationFailures);
    }
  };

  const proceedToSave = () => {
    if (shouldConfirmCancelReason) {
      setCancelReasonConfirmState("confirm");

      return;
    }

    performSave();
  };

  const handleSaveSchoolCandidate = async () => {
    const isAuthorizedCounty = authCounties?.some(
      (county) => county.id === schoolCandidate.uoguCountyId
    );

    if (!isAuthorizedCounty) {
      setShowCountyWarningModal(true);

      return;
    }

    proceedToSave();
  };

  const handleConfirmCountyChange = async () => {
    setShowCountyWarningModal(false);
    proceedToSave();
  };

  const handleCancelCountyChange = () => {
    setShowCountyWarningModal(false);
  };

  const handleCancelReasonConfirmCancel = () => {
    setCancelReasonConfirmState(undefined);
  };

  const handleCancelReasonConfirmOk = async () => {
    setCancelReasonConfirmState(undefined);
    await performSave();
  };

  const handleInstitutionChange = (e: any) => {
    const institutionId = Number.parseInt(e.target.value);
    if (!isNaN(institutionId)) {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        institutionId: institutionId,
      };
      setSchoolCandidate(newSchoolCandidate);
    } else {
      const newSchoolCandidate: SchoolCandidate = {
        ...schoolCandidate,
        institutionId: undefined,
      };
      setSchoolCandidate(newSchoolCandidate);
    }
    dispatch(setIsEdited());
  };

  const handleApplyOrRefreshOibData = async () => {
    setShowOibAlert(undefined);
    if (schoolCandidate.isOibConnected && !oibServiceResult) {
      // Refresh case
      const oibIdentification: OibIdentification = {
        id: schoolCandidate.id,
        oib: schoolCandidate.oib,
        firstName: schoolCandidate.firstName,
        lastName: schoolCandidate.lastName,
      };

      const isOibRefreshSuccessful = await updateOibSystemData(
        oibIdentification,
        true
      );

      if (isOibRefreshSuccessful) {
        setShowOibSuccess(t("common.oibServiceResultStatus.successRefresh"));
      } else {
        setShowOibAlert(t("common.oibServiceResultStatus.oibFailed"));
      }
    } else if (oibServiceResult) {
      // Apply case
      const newSchoolCandidate = mapOibDataToSchoolCandidate(
        schoolCandidate,
        oibServiceResult
      );

      setSchoolCandidate(newSchoolCandidate);
      dispatch(setIsEdited());
      setOibServiceResult(undefined);
      setShowOibSuccess(t("common.oibServiceResultStatus.successApply"));
    }
  };

  const personData = useMemo(
    () =>
      buildOibPersonDataFromSchoolCandidate({
        schoolCandidate,
        alternativeCodeTypes: schoolCandidateAlternativeCodeTypes,
        alternativeCodeReasons: schoolCandidateAlternativeCodeReasons,
        handleFieldChange,
      }),
    [
      schoolCandidate,
      schoolCandidateAlternativeCodeTypes,
      schoolCandidateAlternativeCodeReasons,
      handleFieldChange,
    ]
  );

  if (isLoading) return null;
  return (
    <ContentArea>
      <ToolbarTop
        showAdd={"hide"}
        showBack={"show"}
        showEdit={"hide"}
        pageTitle={t("navigation.schoolCandidateList")}
        errors={errors}
      />
      <FormCard
        cardTitle={t(
          "common.schoolCandidateModel.schoolCandidatePersonalDetails"
        )}
        labelStatus={{
          color: FormatUtils.getSchoolCandidateStatusColor(
            schoolCandidate.statusId
          ),
          name:
            statuses.find((v) => v.id === schoolCandidate.statusId)?.name ?? "",
        }}
      >
        <OibIdentitySection
          disabled={hasRequestOrDraft}
          isOibConnected={schoolCandidate.isOibConnected}
          oibValue={schoolCandidate.oib}
          onOibChange={(value) => {
            handleFieldChange("oib", value, {
              allowEmpty: true,
              customMapping: (oibValue) =>
                oibValue.length > 0 ? oibValue : undefined,
            });

            setShowOibAlert(undefined);
            setShowOibSuccess(undefined);
          }}
          oibErrorText={
            errors?.find((error) => error.propertyName === "Person.OIB")
              ?.message
          }
          isOibReadonly={
            schoolCandidate.isOibConnected && !canInstitutionUserEdit
          }
          showConnectButton={
            (!isConnected || isEditingExistingCandidate) &&
            !canInstitutionUserEdit
          }
          onConnectClick={handleConnectDisconnectOibSystem}
          isConnected={isConnected && isEditingExistingCandidate}
          oibSecondaryAction={
            isConnected &&
            isEditingExistingCandidate &&
            !canInstitutionUserEdit && (
              <Button
                onClick={handleApplyOrRefreshOibData}
                disabled={
                  !(
                    schoolCandidate.oib !== undefined &&
                    OIB_REGEX.test(schoolCandidate.oib)
                  ) || hasRequestOrDraft
                }
                outline={true}
                size="sm"
              >
                {oibServiceResult
                  ? t("common.schoolCandidateModel.apply")
                  : t("common.schoolCandidateModel.refreshOibData")}
              </Button>
            )
          }
          alternativeCodeErrorText={
            errors?.find(
              (error) =>
                error.propertyName === "Person.AlternativeDocumentNumber"
            )?.message
          }
          alternativeTypeErrorText={
            errors?.find(
              (error) =>
                error.propertyName === "Person.AlternativeDocumentTypeId"
            )?.message
          }
          alternativeReasonErrorText={
            errors?.find(
              (error) =>
                error.propertyName === "Person.AlternativeDocumentReasonId"
            )?.message
          }
          lastNameErrorText={
            errors?.find((error) => error.propertyName === "LastName")?.message
          }
          isLastNameReadonly={
            schoolCandidate.isOibConnected || canInstitutionUserEdit
          }
          lastNameHelper={
            oibServiceResult &&
            renderOibServiceInfoComponent({
              message: oibServiceResult.item?.lastName,
              clickHandler: () =>
                handleFieldChange("lastName", oibServiceResult.item?.lastName),
              currentValue: schoolCandidate.lastName,
              oibValue: oibServiceResult.item?.lastName,
            })
          }
          firstNameErrorText={
            errors?.find((error) => error.propertyName === "FirstName")?.message
          }
          isFirstNameReadonly={
            schoolCandidate.isOibConnected || canInstitutionUserEdit
          }
          firstNameHelper={
            oibServiceResult &&
            renderOibServiceInfoComponent({
              message: oibServiceResult.item?.firstName,
              clickHandler: () =>
                handleFieldChange(
                  "firstName",
                  oibServiceResult.item?.firstName
                ),
              currentValue: schoolCandidate.firstName,
              oibValue: oibServiceResult.item?.firstName,
            })
          }
          personData={personData}
        />

        <Row>
          <Col md={5}>
            {showOibAlert && !showOibSuccess && (
              <InfoPanel
                color="danger"
                message=""
                dismissible={false}
                fullWidth={true}
              >
                {showOibAlert}
              </InfoPanel>
            )}
            {showOibSuccess && !showOibAlert && (
              <InfoPanel
                color="success"
                message=""
                dismissible={false}
                fullWidth={true}
              >
                {showOibSuccess}
              </InfoPanel>
            )}
          </Col>

          {!schoolCandidate.isOibConnected &&
            isEditingExistingCandidate &&
            oibServiceResult && (
              <Col md={4}>
                <IconWrapper color="primary">
                  <Info />
                </IconWrapper>
                <Typography color="primary">
                  {t("common.schoolCandidateModel.disconnectedOibSystem")}
                </Typography>
              </Col>
            )}
        </Row>
        <Row>
          <Col md={3}>
            <DatePicker
              label={t("common.schoolCandidateModel.dateOfBirth")}
              name={"schoolCandidate.dateOfBirth"}
              onChange={(e) =>
                handleFieldChange("dateOfBirth", new Date(e.target.value))
              }
              disabled={
                schoolCandidate.isOibConnected ||
                canInstitutionUserEdit ||
                hasRequestOrDraft
              }
              value={new Date(schoolCandidate.dateOfBirth)}
              errorText={
                errors?.find((e) => e.propertyName === "DateOfBirth")?.message
              }
            />
            {oibServiceResult &&
              renderOibServiceInfoComponent({
                message: hrDate(
                  new Date(oibServiceResult.item?.dateOfBirth ?? new Date())
                ),
                clickHandler: () =>
                  handleFieldChange(
                    "dateOfBirth",
                    new Date(oibServiceResult.item!.dateOfBirth)
                  ),
                currentValue: new Date(schoolCandidate.dateOfBirth),
                oibValue: oibServiceResult.item?.dateOfBirth
                  ? new Date(oibServiceResult.item.dateOfBirth)
                  : null,
              })}
          </Col>
          <Col md={3}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateGender")}
              name={"schoolCandidate.gender"}
              value={schoolCandidate.genderId}
              onChange={(e) =>
                handleFieldChange("genderId", e.target.value, {
                  parseAsNumber: true,
                })
              }
              selectData={codeBookToSelectData(genders)}
              disabled={
                schoolCandidate.isOibConnected ||
                canInstitutionUserEdit ||
                hasRequestOrDraft
              }
              borderless={
                schoolCandidate.isOibConnected ||
                canInstitutionUserEdit ||
                hasRequestOrDraft
              }
              errorText={
                errors?.find((e) => e.propertyName === "GenderId")?.message
              }
            />
            {oibServiceResult &&
              renderOibServiceInfoComponent({
                message: genders?.find(
                  (v) => v.id === oibServiceResult.item?.genderId
                )?.name,
                clickHandler: () =>
                  handleFieldChange(
                    "genderId",
                    oibServiceResult.item!.genderId,
                    {
                      parseAsNumber: true,
                    }
                  ),
                currentValue: schoolCandidate.genderId,
                oibValue: oibServiceResult.item?.genderId,
              })}
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.fatherName")}
              value={schoolCandidate.fatherName}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              onChange={(e) => handleFieldChange("fatherName", e.target.value)}
              errorText={
                errors?.find((e) => e.propertyName === "FatherName")?.message
              }
            />
            {oibServiceResult &&
              renderOibServiceInfoComponent({
                message: oibServiceResult.item?.fatherName,
                clickHandler: () =>
                  handleFieldChange(
                    "fatherName",
                    oibServiceResult.item?.fatherName
                  ),
                currentValue: schoolCandidate.fatherName,
                oibValue: oibServiceResult.item?.fatherName,
              })}
          </Col>
          <Col md={3}>
            <TextField
              label={t("common.schoolCandidateModel.motherName")}
              value={schoolCandidate.motherName}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              onChange={(e) => handleFieldChange("motherName", e.target.value)}
              errorText={
                errors?.find((e) => e.propertyName === "MotherName")?.message
              }
            />
            {oibServiceResult &&
              renderOibServiceInfoComponent({
                message: oibServiceResult.item?.motherName,
                clickHandler: () =>
                  handleFieldChange(
                    "motherName",
                    oibServiceResult.item?.motherName
                  ),
                currentValue: schoolCandidate.motherName,
                oibValue: oibServiceResult.item?.motherName,
              })}
          </Col>
        </Row>
      </FormCard>
      <AddressSelect
        title={t("common.schoolCandidateModel.permanentAddress")}
        value={schoolCandidate.residence?.permanentAddress}
        infoValue={oibServiceResult?.item?.residence?.permanentAddress}
        handleChange={(a) => handlePermanentAddressChange(a)}
        readOnly={canInstitutionUserEdit || hasRequestOrDraft}
        disabled={
          schoolCandidate.isOibConnected ||
          canInstitutionUserEdit ||
          hasRequestOrDraft
        }
        errorText={
          errors?.find((e) => e.propertyName === "PermanentAddressId")?.message
        }
      >
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.schoolCandidateModel.permanentAddressNote")}
              value={schoolCandidate.permanentAddressNote ?? ""}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              onChange={(e) =>
                handleFieldChange("permanentAddressNote", e.target.value)
              }
            />
          </Col>
        </Row>
      </AddressSelect>
      <AddressSelect
        title={t("common.schoolCandidateModel.temporaryAddress")}
        value={schoolCandidate.residence?.temporaryAddress}
        infoValue={oibServiceResult?.item?.residence?.temporaryAddress}
        handleChange={(a) => handleTemporaryAddressChange(a)}
        readOnly={canInstitutionUserEdit || hasRequestOrDraft}
        disabled={
          schoolCandidate.isOibConnected ||
          canInstitutionUserEdit ||
          hasRequestOrDraft
        }
        errorText={
          errors?.find((e) => e.propertyName === "TemporaryAddressId")?.message
        }
      />

      <FormCard cardTitle={t("common.schoolCandidateModel.otherDetails")}>
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.schoolCandidateAddReason")}
              name={"schoolCandidate.schoolCandidateAddReason"}
              value={schoolCandidate.schoolCandidateAddReasonId}
              onChange={(e) =>
                handleFieldChange(
                  "schoolCandidateAddReasonId",
                  e.target.value,
                  {
                    parseAsNumber: true,
                    allowEmpty: true,
                    customMapping: (value) =>
                      Number.isNaN(Number.parseInt(value))
                        ? null
                        : Number.parseInt(value),
                  }
                )
              }
              selectData={codeBookToSelectData(schoolCandidateAddReasons)}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              borderless={canInstitutionUserEdit || hasRequestOrDraft}
              emptyOption={{ value: undefined, text: undefined }}
              errorText={
                errors?.find((e) => e.propertyName === "AddReasonId")?.message
              }
            />
          </Col>
          <Col md={6}>
            <TextField
              label={t(
                "common.schoolCandidateModel.schoolCandidateAddReasonOther"
              )}
              value={schoolCandidate.schoolCandidateAddReasonOther ?? ""}
              onChange={(e) =>
                handleFieldChange(
                  "schoolCandidateAddReasonOther",
                  e.target.value,
                  {
                    allowEmpty: true,
                    customMapping: (value) =>
                      value && value.length > 0 ? value : null,
                  }
                )
              }
              disabled={
                schoolCandidate.schoolCandidateAddReasonId !== 3 ||
                canInstitutionUserEdit ||
                hasRequestOrDraft
              }
              errorText={
                errors?.find((e) => e.propertyName === "AddReasonOther")
                  ?.message
              }
            />
          </Col>
        </Row>
        {isEditingExistingCandidate && (
          <Row>
            <Col md={6}>
              <Select
                label={t(
                  "common.schoolCandidateModel.schoolCandidateCancelReason"
                )}
                name={"schoolCandidate.schoolCandidateCancelReason"}
                value={schoolCandidate.schoolCandidateCancelReasonId}
                onChange={(e) =>
                  handleFieldChange(
                    "schoolCandidateCancelReasonId",
                    e.target.value,
                    {
                      parseAsNumber: true,
                      allowEmpty: true,
                      customMapping: (value) =>
                        Number.isNaN(Number.parseInt(value))
                          ? null
                          : Number.parseInt(value),
                    }
                  )
                }
                selectData={codeBookToSelectData(schoolCandidateCancelReasons)}
                emptyOption={{ value: undefined, text: undefined }}
                errorText={
                  errors?.find((e) => e.propertyName === "CancelReasonId")
                    ?.message
                }
              />
            </Col>
            <Col md={6}>
              <TextField
                label={t(
                  "common.schoolCandidateModel.schoolCandidateCancelReasonOther"
                )}
                value={schoolCandidate.schoolCandidateCancelReasonOther ?? ""}
                onChange={(e) =>
                  handleFieldChange(
                    "schoolCandidateCancelReasonOther",
                    e.target.value,
                    {
                      allowEmpty: true,
                      customMapping: (value) =>
                        value && value.length > 0 ? value : null,
                    }
                  )
                }
                disabled={
                  schoolCandidate.schoolCandidateCancelReasonId !== 3
                }
                errorText={
                  errors?.find((e) => e.propertyName === "CancelReasonOther")
                    ?.message
                }
              />
            </Col>
          </Row>
        )}
        <Row>
          <Col md={6}>
            <Select
              label={t("common.schoolCandidateModel.uoguCounty")}
              name={"schoolCandidate.uoguCounty"}
              required={true}
              value={schoolCandidate?.uoguCountyId}
              onChange={handleUOGUCountyChange}
              selectData={codeBookToSelectData(allCounties)}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              borderless={canInstitutionUserEdit || hasRequestOrDraft}
              errorText={
                errors?.find((e) => e.propertyName === "UoguCountyId")?.message
              }
            />
          </Col>
          <Col md={6}>
            <InstitutionSelect
              label={t("common.schoolCandidateModel.institutionList")}
              name={"schoolCandidate.institution"}
              value={schoolCandidate.institutionId}
              onlyByEnrollmentInstitution
              excludeHandicapped
              excludePrivate={false}
              excludeSatellite={false}
              onChange={handleInstitutionChange}
              disabled={
                (isEditingExistingCandidate &&
                  schoolCandidate.statusId !== SchoolCandidateStatus.notSent) ||
                canInstitutionUserEdit ||
                hasRequestOrDraft
              }
              countyId={schoolCandidate?.uoguCountyId}
              showUser={true}
              errorText={
                errors?.find((e) => e.propertyName === "InstitutionId")?.message
              }
            ></InstitutionSelect>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <Checkbox
              label={t("common.schoolCandidateModel.isHandicapped")}
              checked={schoolCandidate.isHandicapped}
              disabled={canInstitutionUserEdit || hasRequestOrDraft}
              onChange={() =>
                handleFieldChange(
                  "isHandicapped",
                  !schoolCandidate.isHandicapped
                )
              }
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showCancel={"show"}
        showSave={"show"}
        onClickSave={handleSaveSchoolCandidate}
        setSaveErrors={setErrors}
      />
      <Row>
        <Col md={12}>&nbsp;</Col>
      </Row>
      <Row>
        <Col md={12}>&nbsp;</Col>
      </Row>
      <Row>
        <Col md={12}>&nbsp;</Col>
      </Row>
      <Row>
        <Col md={12}>&nbsp;</Col>
      </Row>

      <Modal
        open={showCountyWarningModal}
        title={t("common.warning")}
        onClose={handleCancelCountyChange}
        onConfirm={handleConfirmCountyChange}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        type="confirm"
      >
        <Typography>
          {t("common.schoolCandidateModel.countyWarningMessage")}
        </Typography>
      </Modal>

      <ConfirmModal
        title={t("common.schoolCandidateModel.cancelReasonSaveModalTitle")}
        confirmState={cancelReasonConfirmState}
        confirmMessage={t(
          "common.schoolCandidateModel.cancelReasonSaveModalMessage"
        )}
        confirmText={t("common.confirm")}
        cancelText={t("common.cancel")}
        onClickClose={handleCancelReasonConfirmCancel}
        onClickConfirm={handleCancelReasonConfirmOk}
      />
    </ContentArea>
  );
}
